import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { ProductHeader } from "../../components/extras/product/ProductHeader";
import { InstaBlock, Tiles } from "../../components/extras/product/Blocks";
import { Accuracy } from "../../components/extras/product/Accuracy";
import { AppTab } from "../../components/extras/product/AppTab";
import { Fixups } from "../../components/extras/product/Fixups";
import { Pencil, Printer, Download } from "lucide-react";
import { media, aud } from "../../components/extras/ExtrasShell";
import { VideoPrompts } from "../../components/VideoPrompts";
import { ContentsTab, FieldsForm, SlidesForm, RegenButton, usePatch } from "../../components/extras/product/Tabs";

import { CoverTicks, CompareButton, ImagesTab, SiteTab } from "../../components/extras/product/More";

const TABS = [["contents", "Contents"], ["listing", "Store listing"], ["instagram", "Instagram details"], ["slides", "Carousel slides"], ["images", "Images"], ["video", "Video prompts"], ["app", "App edition"], ["site", "Site connection"]];

const BundleItems = ({ p }) => (
  <div data-testid="ex-bundle-items" className="ex-card p-6">
    <div className="ex-eyebrow mb-4">In this bundle · {p.discount}% off</div>
    {(p.bundle_items || []).map((it) => <div key={it.id} className="flex justify-between text-sm py-2 border-b border-[#EFE9E0]"><span>{it.name}</span><span className="ex-mono text-xs">{aud(it.price_aud)}</span></div>)}
    <div className="flex justify-between text-sm pt-3"><span className="text-stone-500">Bought separately {aud(p.bundle_total)}</span><span className="ex-display text-xl">{aud(p.bundle_price)}</span></div>
  </div>
);
const STAGES = [["content", "Writing the guide"], ["listing", "Listing, Instagram & carousel"], ["image", "Instagram picture"], ["pdf", "PB PDF"], ["done", "Ready"]];

const Making = ({ p, reload }) => {
  const at = STAGES.findIndex(([k]) => k === p.stage);
  const retry = async () => { try { await http.post(`/extras/products/${p.id}/retry`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="ex-making" className="ex-card p-8">
      <div className="flex items-center gap-3 mb-6">
        {p.status === "failed" ? <span className="text-red-700 font-medium">Stopped: {p.error}</span> : <><Loader2 className="animate-spin text-[#8B6B4A]" size={18} /><span className="font-medium">{p.progress}</span></>}
        {p.status === "failed" && <button data-testid="ex-retry" onClick={retry} className="ex-btn ex-btn-dark ml-auto"><RotateCw size={14} /> Carry on</button>}
      </div>
      <ol className="grid md:grid-cols-5 gap-3">
        {STAGES.map(([k, l], i) => (
          <li key={k} className={`rounded-md border px-3 py-2 text-sm ${i < at ? "border-emerald-200 bg-emerald-50 text-emerald-800" : i === at ? "border-[#8B6B4A] bg-[#F1ECE4]" : "border-stone-200 text-stone-400"}`}>{i < at ? "✓ " : ""}{l}</li>
        ))}
      </ol>
      <p className="text-xs text-stone-500 mt-4">Usually 2–5 minutes. You can leave this page — it carries on, and picks up where it left off if the server restarts.</p>
    </div>
  );
};

export default function ExtrasProduct() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [spend, setSpend] = useState(0);
  const [tab, setTab] = useState("contents");
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => {
    http.get(`/extras/products/${id}`).then(({ data }) => setP(data)).catch((e) => toast.error(errMsg(e)));
    http.get(`/extras/products/${id}/usage`).then(({ data }) => setSpend(data.total)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!p || (p.status !== "generating" && !p.busy)) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [p, load]);
  useEffect(() => { if (p && !p.content && tab === "contents") setTab("listing"); }, [p, tab]);
  const save = usePatch(p || {}, setP);
  if (!p) return <div className="text-stone-500">Loading…</div>;
  return (
    <div data-testid="ex-product" className="space-y-8 ex-fade">
      <ProductHeader p={p} setP={setP} spend={spend} />
      {p.error && p.status !== "failed" && <div data-testid="ex-product-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-2">{p.error}</div>}
      {p.status !== "ready" && <Making p={p} reload={load} />}
      {p.content && p.status === "ready" && <Accuracy p={p} reload={load} />}
      {p.product_type === "bundle" && <BundleItems p={p} />}
      {p.instagram && <InstaBlock p={p} reload={load} />}
      {p.deliverable_id && <Tiles p={p} reload={load} />}
      {(p.content || p.listing) && (
        <div>
          <div className="flex flex-wrap items-center gap-1 border-b border-[#E6DDCF] mb-5">
            {TABS.filter(([k]) => (p.content ? true : ["listing", "instagram", "slides", "site", "video"].includes(k))).map(([k, l]) => (
              <button key={k} data-testid={`ex-tab-${k}`} onClick={() => { setTab(k); setEditing(false); }} className={`px-3 py-2.5 text-sm -mb-px border-b-2 transition-colors ${tab === k ? "border-[#30271D] text-[#2B241D] font-medium" : "border-transparent text-stone-500 hover:text-stone-800"}`}>{l}</button>
            ))}
            <div className="ml-auto flex gap-2 pb-1.5">
              {tab === "contents" && p.content && <button data-testid="ex-edit-toggle" onClick={() => setEditing(!editing)} className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs ${editing ? "bg-[#30271D] text-white border-[#30271D]" : "border-stone-300 bg-white hover:bg-stone-50"}`}><Pencil size={12} /> Edit</button>}
              {p.deliverable_id && <a data-testid="ex-print" href={media(p.deliverable_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-stone-300 bg-white px-3 py-1.5 text-xs hover:bg-stone-50"><Printer size={12} /> Print</a>}
            </div>
          </div>
          {tab === "contents" && p.content && <CoverTicks key={`t${p.updated_at}`} p={p} save={save} />}
          {tab === "contents" && p.original_pdf_id && <div data-testid="ex-restyled-note" className="ex-card px-5 py-4 mb-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-stone-600"><span>Restyled from your uploaded PDF <b className="text-stone-800">{p.pdf_name}</b>.</span>
            <CompareButton p={p} />
            <a data-testid="ex-download-original" href={`${media(p.original_pdf_id)}?download=1`} className="inline-flex items-center gap-1.5 text-sm text-[#8B6B4A] hover:underline"><Download size={14} /> Original upload</a></div>}
          {tab === "contents" && p.original_pdf_id && p.content && <Fixups p={p} setP={setP} reload={load} />}
          {tab === "contents" && p.content && <ContentsTab p={p} save={save} editing={editing} setEditing={setEditing} />}
          {tab === "app" && <AppTab p={p} setP={setP} reload={load} />}
          {tab === "video" && <VideoPrompts data={p} path={`/extras/products/${p.id}/video-prompts`} reload={load} />}
          {tab === "images" && <ImagesTab key={`im${p.updated_at}`} p={p} save={save} />}
          {tab === "site" && <SiteTab p={p} />}
          {tab === "listing" && p.listing && <FieldsForm key={`l${p.updated_at}`} testid="ex-listing-form" value={p.listing} onSave={(v) => save({ listing: v })} extra={<RegenButton p={p} part="listing" reload={load} testid="ex-listing-regen" />} />}
          {tab === "instagram" && p.instagram && <FieldsForm key={`i${p.updated_at}`} testid="ex-insta-form" value={p.instagram} onSave={(v) => save({ instagram: { ...v, hashtags: (v.hashtags || []).map((h) => h.replace(/^#/, "")) } })} />}
          {tab === "slides" && p.carousel?.slides && <SlidesForm key={`s${p.updated_at}`} p={p} save={save} reload={load} />}
        </div>
      )}
    </div>
  );
}
